import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import LogoutIcon from "@mui/icons-material/Logout";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { Container } from "@mui/system";
import { Typography } from "@mui/material";
import { useContext } from "react";
import AuthContext from "../../context/AuthContext";

interface ButtonProps {
  title: string;
  action: () => void;
  icon: JSX.Element;
  disabled?: Boolean;
}

interface Props {
  buttons: ButtonProps[];
  title: string;
}

const iconStyles = {
  backgroundColor: "rgb(251,171,53, 0.9)",
  padding: "4px",
  color: "white",
  marginRight: "5px",
  borderRadius: "10px",
  "&:hover": {
    backgroundColor: "rgb(140,150,169)",
  },
};

export function DesktopBar({ title, buttons }: Props) {
  const { onLogout, isDrawerOpen } = useContext(AuthContext);

  const drawerWidth = 250;
  const collapsedWidth = 64;

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: "rgba(140, 150, 169, 0.9)",
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        position: "fixed",
        top: 0,
        left: isDrawerOpen ? drawerWidth : collapsedWidth,
        width: isDrawerOpen
          ? `calc(100% - ${drawerWidth}px)`
          : `calc(100% - ${collapsedWidth}px)`,
        right: 0,
        zIndex: 1000,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: "0 4px 20px rgba(140, 150, 169, 0.2)",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: "64px !important" }}>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              display: {
                xs: "none",
                sm: "block",
              },
              color: "white",
              fontWeight: 600,
              fontSize: "1.25rem",
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", md: "flex", justifyContent: "end" },
              gap: 1,
            }}
          >
            {buttons.map((button, index) => (
              <Button
                startIcon={button.icon}
                key={index}
                onClick={button.action}
                disabled={!!button.disabled}
                variant="outlined"
                sx={{
                  color: "white",
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "12px",
                  padding: "8px 16px",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    borderColor: "rgb(251, 171, 53)",
                    backgroundColor: "rgba(251, 171, 53, 0.2)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(251, 171, 53, 0.3)",
                  },
                  "&:disabled": {
                    color: "rgba(255, 255, 255, 0.5)",
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                {button.title}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: 0, ml: 2 }}>
            <Tooltip title="Logout" arrow>
              <IconButton
                onClick={onLogout}
                sx={{
                  ...iconStyles,
                  background:
                    "linear-gradient(135deg, rgb(251, 171, 53) 0%, rgba(251, 171, 53, 0.8) 100%)",
                  boxShadow: "0 4px 12px rgba(251, 171, 53, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, rgba(251, 171, 53, 0.9) 0%, rgb(251, 171, 53) 100%)",
                    transform: "scale(1.1) translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(251, 171, 53, 0.4)",
                  },
                }}
                size="medium"
              >
                <LogoutIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
